<template>
  <div class="setting-content--section">
    <TitleBar v-if="currentUser.is_accountant">
      <template v-slot:title>
        <h1>{{ $gettext('My personal profile') }}</h1>
      </template>
    </TitleBar>
    <div class="content">
      <div style="max-width: 1024px; width: 100%;">
        <div v-if="currentUser.is_accountant" style="margin-bottom: 2rem;">
          <h3>{{ $gettext('Profile settings') }}</h3>
        </div>
        <b-form @submit="onSubmit">
          <b-form-group
            id="input-group-1"
            label-cols-sm="3"
            label-align-sm="right"
            content-cols-md="5"
            label-class="gk-label-margin"
            :label="$gettext('Profile picture')"
            label-for="input-1"
          >
            <VueDropzone
              :preloaded_file="imageSrc"
              :upload_url="Urls['profile-detail'](form.profile.id)"
              image_field_name="avatar"
            ></VueDropzone>
          </b-form-group>
          <b-form-group
            id="input-group-1"
            label-cols-sm="3"
            label-align-sm="right"
            content-cols-md="5"
            label-class="gk-label-margin"
            :label="$gettext('Full name*')"
            label-for="input-1"
          >
            <b-form-input
              data-analytics-click
              id="input-1"
              v-model="form.profile.full_name"
              required
              :placeholder="$gettext('John Doe')"
              @change="onFormChange"
            ></b-form-input>
          </b-form-group>
          <b-form-group
            id="input-group-2"
            label-cols-sm="3"
            label-align-sm="right"
            content-cols-md="5"
            label-class="gk-label-margin"
            :label="$gettext('Email*')"
            label-for="input-2"
            :description="
              $gettext(
                'Any attachments forwarded from this email address to upload@costs.getgekko.com will be uploaded to your account.'
              )
            "
          >
            <b-form-input
              id="input-2"
              v-model="form.user.email"
              type="email"
              :placeholder="$gettext('john.doe@email.com')"
              @change="onEmailChange"
            ></b-form-input>
            <EmailValidityHint variant="boxy" show-link />
          </b-form-group>
          <b-form-group
            id="input-group-3"
            label-cols-sm="3"
            label-align-sm="right"
            content-cols-md="5"
            label-class="gk-label-margin"
            :label="$gettext('Password')"
            label-for="input-3"
          >
            <a id="input-3" class="btn btn-block btn-outline-primary" :href="Urls['accounts_change_password']()" v-translate
              >Change password</a
            >
          </b-form-group>
          <b-form-group
            id="input-group-4"
            label-cols-sm="3"
            label-align-sm="right"
            content-cols-md="5"
            label-class="gk-label-margin"
            :label="$gettext('Date of birth')"
            label-for="input-4"
          >
            <b-form-datepicker
              id="input-4"
              v-model="form.profile.date_birth"
              no-flip
              initial-date="1990-01-01"
              @change="onFormChange"
              :label-no-date-selected="$gettext('No date selected')"
            ></b-form-datepicker>
          </b-form-group>
          <b-form-group
            id="input-group-5"
            label-cols-sm="3"
            label-align-sm="right"
            content-cols-md="5"
            label-class="gk-label-margin"
            :label="$gettext('BSN')"
            label-for="input-5"
            :description="$gettext('Your personal government number')"
          >
            <b-form-input
              data-analytics-click="x"
              id="input-5"
              v-model="form.profile.bsn"
              :placeholder="$gettext('123456789')"
              @change="onFormChange"
            ></b-form-input>
          </b-form-group>
          <b-form-group
            id="input-group-6"
            label-cols-sm="3"
            label-align-sm="right"
            content-cols-md="5"
            label-class="gk-label-margin"
            :label="$gettext('Preferred language') + '*'"
            label-for="input-6"
          >
            <b-form-select
              id="input-6"
              v-model="form.profile.preferred_language"
              :options="language_choices"
              @change="onLanguageChange"
              required
            ></b-form-select>
          </b-form-group>
          <b-form-group
            id="input-group-7"
            label-cols-sm="3"
            label-align-sm="right"
            content-cols-md="5"
            label-class="gk-label-margin"
            :label="$gettext('Default company')"
            label-for="input-7"
            :description="
              $gettext(
                'All receipts forwarded to upload@costs.getgekko.com will be added to this specific company profile.'
              )
            "
          >
            <b-form-select
              id="input-7"
              v-model="form.profile.default_company"
              value-field="id"
              text-field="name"
              :options="companies"
              @change="onFormChange"
            ></b-form-select>
          </b-form-group>
          <b-alert
            :show="showDismissibleErrorCountdown"
            class="float-alert-top"
            variant="danger"
            dismissible
            @dismissed="showDismissibleErrorCountdown = 0"
          >
            <span v-for="[key, value] in Object.entries(errors)" :key="key">{{ key }} - {{ value[0] }}</span>
          </b-alert>
          <b-alert
            :show="showDismissibleCountdown"
            class="float-alert-top"
            variant="success"
            dismissible
            @dismissed="showDismissibleCountdown = 0"
          >
            {{ $gettext('Settings successfully updated!') }}
          </b-alert>
          <b-alert
            :show="showDismissibleCountdownEmailError"
            class="float-alert-top"
            variant="danger"
            dismissible
            @dismissed="showDismissibleCountdownEmailError = 0"
          >
            {{ $gettext('Email could not be validated. Please make sure it is a valid email.') }}
          </b-alert>
          <b-form-group
            id="input-group-8"
            label-cols-sm="3"
            label-align-sm="right"
            content-cols-md="5"
            label-class="gk-label-margin"
            label=""
            label-for="input-8"
          >
            <GekkoButton
              :href="Urls['accounts_delete_profile']()"
              :text="$gettext('Delete Account')"
              icon="fa-solid fa-trash-can"
              type="submit"
              variant="link"
              class="mr-2 text-danger"
            />
            <GekkoButton
              :text="$gettext('Save Settings')"
              icon="fa-solid fa-floppy-disk"
              type="submit"
              class="position-relative"
              :loading="isWaitingForResponse"
            />
          </b-form-group>
        </b-form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import ProfileService from '@/store/services/profile.service.js'
import UserService from '@/store/services/user.service.js'
import CompanyService from '@/store/services/company.service.js'

import VueDropzone from '@/components/VueDropzone'
import EmailValidityHint from '@/components/EmailValidityHint'
import GekkoButton from '@/components/GekkoButton'
import TitleBar from '@/components/TitleBar'

// mixins
import settingsPageSetup from '@/mixins/settingsPageSetup'

export default {
  name: 'ProfileSettings',
  components: {
    VueDropzone,
    EmailValidityHint,
    GekkoButton,
    TitleBar
  },
  mixins: [settingsPageSetup],
  data() {
    return {
      showDismissibleCountdown: 0,
      showDismissibleCountdownEmailError: 0,
      showDismissibleErrorCountdown: 0,
      pageSettings: {
        title: this.$gettext('My personal profile'),
        showCompanyPicker: false,
      },
      formChanged: false,
      emailChanged: false,
      languageChanged: false,
      form: {
        profile: {
          id: '',
          full_name: '',
          date_birth: '',
          bsn: '',
          preferred_language: '',
          default_company: '',
          user: 0,
        },
        user: {
          email: '',
        },
      },
      errors: {},
      language_choices: [
        { value: 'en', text: 'English' },
        { value: 'nl', text: 'Nederlands' },
        { value: 'es', text: 'Español' },
        { value: 'pl', text: 'Polski' },
      ],
      companies: [],
      imageSrc: '',
      verificationEmailSent: false,
    }
  },
  computed: {
    ...mapGetters({
      currentUser: 'main/getCurrentUser',
      currentUserProfile: 'main/getCurrentUserProfile',
      isWaitingForResponse: 'main/isWaitingForResponse',
    }),
  },
  created() {
    this.form = {
      profile: {
        id: this.currentUserProfile.id,
        full_name: this.currentUserProfile.full_name,
        date_birth: this.currentUserProfile.date_birth,
        bsn: this.currentUserProfile.bsn,
        preferred_language: this.currentUserProfile.preferred_language,
        default_company: this.currentUserProfile.default_company,
        avatar: null,
        user: this.currentUser.id,
      },
      user: { email: this.currentUser.email },
    }
    if (this.currentUserProfile.avatar) {
      this.imageSrc = this.currentUserProfile.avatar
    }

    CompanyService.list().then((response) => {
      this.companies = response.data
    })
  },
  methods: {
    ...mapMutations({
      updateStoredProfile: 'main/SET_CURRENT_USER_PROFILE',
      updateUserEmail: 'main/SET_CURRENT_USER_EMAIL',
      updatePreferredLanguage: 'main/SET_PREFERRED_LANGUAGE',
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE',
    }),
    onSubmit(e) {
      e.preventDefault()
      this.setWaitingForResponse(true)
      //create or edit
      if (this.form.profile.avatar === null) {
        delete this.form.profile.avatar
      }
      if (this.emailChanged) {
        UserService.updateEmail({ email: this.form.user.email })
          .then((response) => {
            this.updateUserEmail(this.form.user.email)
          })
          .catch((error) => {
            if (error.status === 404) {
              this.showDismissibleCountdownEmailError = 2
            }
          })
      }
      ProfileService.update(this.form.profile.id, this.form.profile)
        .then((response) => {
          console.log('profile updated', response.data)
          this.updateStoredProfile(response.data)

          this.showDismissibleCountdown = 2
          this.formChanged = false
          if (this.languageChanged) {
            this.updatePreferredLanguage(this.form.profile.preferred_language)
            window.preferred_language = this.form.profile.preferred_language
            this.$language.current = this.form.profile.preferred_language
            this.$router.push({
              name: this.$route.name,
              params: {
                ...this.$route.params,
                language: this.$language.current,
              },
              query: this.$route.query,
            })
          }
        })
        .catch((error) => {
          if (error.status === 400) {
            this.showDismissibleErrorCountdown = 6
            this.errors = error.data
          }
        })
        .finally(() => {
          this.setWaitingForResponse(false)
        })
    },
    onFormChange() {
      this.formChanged = true
    },
    onLanguageChange() {
      this.languageChanged = true
    },
    onEmailChange() {
      this.emailChanged = true
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.formChanged) {
      const ConfirmModalConstructor = this.$options.components['confirm-modal']
      const confirmModalInstance = new ConfirmModalConstructor({
        propsData: {
          title: this.$gettext('Leaving this page'),
          message: this.$gettext(
            'You made changes that have not yet been saved. Are you sure you want to continue and discard those changes?'
          ),
          options: {
            variant: 'primary',
            confirmText: this.$gettext('Ok'),
          },
        },
      })
      confirmModalInstance.$mount()
      document.body.appendChild(confirmModalInstance.$el)
      confirmModalInstance.$on('confirmed', () => {
        if (confirmModalInstance && confirmModalInstance.$el.parentNode) {
          confirmModalInstance.$el.parentNode.removeChild(confirmModalInstance.$el)
        }
        next()
      })
      confirmModalInstance.$on('cancel', () => {
        if (confirmModalInstance && confirmModalInstance.$el.parentNode) {
          confirmModalInstance.$el.parentNode.removeChild(confirmModalInstance.$el)
        }
      })
    } else {
      next()
    }
  },
}
</script>

<style lang="scss">
.float-alert-top {
  right: 40%;
  position: fixed !important;
  top: 50px;
  margin: auto;
  z-index: 1000;
  font-size: 24px;
}
</style>
